import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@app/context/AuthContext'
import { useArabicSessionLanguage } from '@app/context/SessionSettingsContext'

import { useTableSearch } from '@ui/v2/Table/hooks/useTableSearch'
import { FilterOptions } from '@ui/v2/TableFilter/TableFilter'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { conditionalReverse } from '@utils/array'
import { formatDate } from '@utils/date.utils'

import { ClientJourneyMapDto } from '@model/ClientJourneyMap'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { IBReportResultsPage } from '../IBReportResultsPage'
import { IBClientJourneyMapSortKeys, IBClientJourneyMapTable } from './IBClientJourneyMapTable'
import { cjmColumns } from './IBClientJourneyMapTable.columns'
import { useIBClientJourneyMapData } from './hooks/useIBClientJourneyMapData'
import { useIBClientJourneyMapSummary } from './hooks/useIBClientJourneyMapSummary'

const useClientJourneyMapSearch = () =>
  useTableSearch<IBClientJourneyMapSortKeys, ClientJourneyMapDto>({
    columns: cjmColumns,
    placeholderKey: 'IB.Client name',
    searchOptions: { customInitialSearchQuery: { ca_search_Name: '' } },
  })

const useCJMGenerateDocumentFile = (data: ClientJourneyMapDto[]) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { formatMoney } = useFormatNumber()
  const isArabic = useArabicSessionLanguage()

  const tableBody = () => {
    const headerCsvData = [
      conditionalReverse(
        [
          t('IB.Client name'),
          t('IB.Dashboard.CJM.Link clicked'),
          t('IB.Registration Date'),
          t('IB.Dashboard.CJM.emailVerified'),
          t('IB.Dashboard.CJM.activated'),
          t('IB.Dashboard.CJM.FTD'),
          t('IB.Dashboard.CJM.FTT'),
          t('IB.Dashboard.CJM.Last Trade'),
          t('Commission'),
        ],
        isArabic
      ),
    ]

    return data.reduce((previousValue, currentValue) => {
      return previousValue.concat([
        conditionalReverse(
          [
            currentValue.name,
            currentValue.linkClickedDate
              ? formatDate(currentValue.linkClickedDate, { formatType: dateFormat })
              : t('N/A'),
            currentValue.registrationDate
              ? formatDate(currentValue.registrationDate, { formatType: dateFormat })
              : t('N/A'),
            currentValue.emailVerifiedDate
              ? formatDate(currentValue.emailVerifiedDate, { formatType: dateFormat })
              : t('N/A'),
            currentValue.activatedDate
              ? formatDate(currentValue.activatedDate, { formatType: dateFormat })
              : t('N/A'),
            currentValue.firstCompletedDepositDate
              ? formatDate(currentValue.firstCompletedDepositDate, { formatType: dateFormat })
              : t('N/A'),
            currentValue.firstClosedTradeDate
              ? formatDate(currentValue.firstClosedTradeDate, { formatType: dateFormat })
              : t('N/A'),
            currentValue.lastClosedTradeDate
              ? formatDate(currentValue.lastClosedTradeDate, { formatType: dateFormat })
              : t('N/A'),
            currentValue.totalCommission ? formatMoney(currentValue.totalCommission) : t('N/A'),
          ],
          isArabic
        ),
      ])
    }, headerCsvData)
  }

  return useGenerateDocumentFile('client-journey-map-report', tableBody())
}

export const IBClientJourneyMapPage: React.FC = () => {
  const { t } = useTranslation()
  const search = useClientJourneyMapSearch()

  const clientJourneyMapTableData = useIBClientJourneyMapData({ search })
  const clientJourneyMapSummary = useIBClientJourneyMapSummary({ search })

  const generateDocumentFile = useCJMGenerateDocumentFile(clientJourneyMapTableData.query.data)

  return (
    <IBReportResultsPage
      title={t('IB.Main touchpoints with clients')}
      data={clientJourneyMapTableData}
      generateDocumentFile={generateDocumentFile}
      search={search}
      summary={
        <TableSummary
          isLoading={clientJourneyMapSummary.isLoading}
          summaryItems={clientJourneyMapSummary.summaryItems}
        />
      }
      scrollToHeaderId={ScrollToIds.IBSignupHeader}
      filterOptions={{}}
      filterOptionsTypes={[
        FilterOptions.LinkClicked,
        FilterOptions.RegistrationStarted,
        FilterOptions.EmailVerified,
        FilterOptions.Activated,
        FilterOptions.FirstTimeDeposit,
        FilterOptions.FirstTimeTrade,
        FilterOptions.LastTrade,
        FilterOptions.TotalCommission,
      ]}
      table={<IBClientJourneyMapTable data={clientJourneyMapTableData} search={search} />}
    />
  )
}
